import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Award } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExperiencePage = () => {
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedExperience, setExpandedExperience] = useState(null);
  const [albums, setAlbums] = useState([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true);
        const data = await api.portfolio.getAll();
        setPortfolioData(data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
        toast({
          title: 'Error',
          description: 'Failed to load portfolio data.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchAlbums = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/albums`);
        setAlbums(response.data);
      } catch (error) {
        console.error('Error fetching albums:', error);
      }
    };

    fetchPortfolio();
    fetchAlbums();
  }, [toast]);

  const scrollToAlbums = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      window.location.href = '/#albums';
    }, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!portfolioData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600">Failed to load data.</p>
      </div>
    );
  }

  return (
    <>
      <section className="py-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Experience</h1>
            <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">8+ Years in International Education</p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="hidden md:block absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200"></div>

            <div className="space-y-8">
              {portfolioData.experience.map((exp, index) => (
                <div key={index} className="relative md:pl-20 pl-0">
                  <div className="hidden md:flex absolute left-6 top-6 w-5 h-5 bg-blue-600 rounded-full border-4 border-white shadow-md z-10"></div>

                  <Card className="border-blue-100 hover:shadow-xl transition-all duration-300 cursor-pointer" onClick={() => setExpandedExperience(expandedExperience === index ? null : index)}>
                    <CardHeader>
                      <div className="flex justify-between items-start gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg text-blue-900 mb-2">{exp.title}</CardTitle>
                          <CardDescription className="text-base font-semibold text-gray-700">{exp.school}</CardDescription>
                          <CardDescription className="text-sm text-gray-500">{exp.location}</CardDescription>
                        </div>
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700 flex-shrink-0">{exp.period}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {exp.highlights.slice(0, expandedExperience === index ? undefined : 2).map((highlight, hIndex) => (
                          <div key={hIndex} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="text-gray-700 text-sm">{highlight}</p>
                          </div>
                        ))}
                        {exp.highlights.length > 2 && (
                          <Button variant="link" className="text-blue-600 p-0 h-auto">
                            {expandedExperience === index ? 'Show Less' : 'Show More'}
                          </Button>
                        )}
                        
                        {index === 0 && albums.length > 0 && (() => {
                          const fairAlbum = albums.find(a =>
                            (a.title || '').toLowerCase().includes('science fair')
                          );
                          if (!fairAlbum) return null;
                          return (
                            <div className="flex justify-center pt-4 mt-4 border-t border-gray-100">
                              <div className="flex flex-col items-center gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/portfolio#album-${fairAlbum._id}`);
                                  }}
                                  className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-full transition-colors shadow-sm"
                                  data-testid="experience-science-fair-link"
                                >
                                  📸 Science Fair '26
                                </button>
                                <span className="text-xs text-gray-500">Click to check photos</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Separator className="max-w-7xl mx-auto" />

      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Award className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Notable Achievements</h2>
          </div>
          <div className="space-y-8">
            {portfolioData.achievements?.map((achievement, index) => (
              <div key={index} className="text-center">
                <p className="text-xl text-blue-100">
                  {achievement.description}
                </p>
                <p className="mt-2 text-blue-200">— {achievement.location}</p>
                {index < portfolioData.achievements.length - 1 && (
                  <div className="w-20 h-0.5 bg-blue-400 mx-auto mt-8"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ExperiencePage;
